import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides } from '../mock';

const HeroSlide = ({ slide, active, onQuote, onServices }) => (
  <div className={`absolute inset-0 transition-opacity duration-1000 ${active ? 'opacity-100' : 'opacity-0'}`}>
    <div className="relative h-full w-full">
      <img src={slide.image} alt={slide.title} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/55 via-slate-900/40 to-slate-900/70" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center px-4 max-w-4xl">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-2xl">
            {slide.title}
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 drop-shadow-lg">
            {slide.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onQuote}
              data-testid="hero-quote-btn"
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Get Free Quote
            </button>
            <button
              onClick={onServices}
              data-testid="hero-services-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Our Services
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SlideDots = ({ slides, current, onSelect }) => (
  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
    {slides.map((slide, index) => (
      <button
        key={slide.id}
        onClick={() => onSelect(index)}
        className={`w-3 h-3 rounded-full transition-all duration-300 ${
          index === current ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
        }`}
        aria-label={`Go to slide ${index + 1}`}
      />
    ))}
  </div>
);

const HeroSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const slideCount = heroSlides.length;

  // `prev` is the functional setState callback parameter, not a closed-over variable.
  // `setCurrentSlide` is a stable React setState reference. Neither belong in deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const advance = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const retreat = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  useEffect(() => {
    // `timer` is a local variable created inside the effect, not a dependency.
    const timer = setInterval(advance, 5000);
    return () => clearInterval(timer);
  }, [advance]);

  return (
    <div id="home" className="relative h-screen w-full overflow-hidden">
      {heroSlides.map((slide, index) => (
        <HeroSlide
          key={slide.id}
          slide={slide}
          active={index === currentSlide}
          onQuote={() => navigate('/contact')}
          onServices={() => navigate('/services')}
        />
      ))}

      <button
        onClick={retreat}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 z-10"
        aria-label="Previous slide"
      >
        <ChevronLeft size={28} />
      </button>
      <button
        onClick={advance}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 z-10"
        aria-label="Next slide"
      >
        <ChevronRight size={28} />
      </button>

      <SlideDots slides={heroSlides} current={currentSlide} onSelect={setCurrentSlide} />
    </div>
  );
};

export default HeroSlideshow;
